import React, { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import About from "./components/About";
import MemoryLane from "./components/MemoryLane";
import BirthdayReveal from "./components/BirthdayReveal";
import AmbientPlayer from "./components/AmbientPlayer";
import Footer from "./components/Footer";
import PasscodeGate from "./components/PasscodeGate";
import VideoWish from "./components/VideoWish";
import WallpaperCard from "./components/WallpaperCard";
import CursorSparkles from "./components/CursorSparkles";
import useLenis from "./hooks/useLenis";
import Admin from "./pages/Admin";
import MemoryDetail from "./pages/MemoryDetail";
import { loadSiteTexts } from "./lib/siteTexts";

const SECTIONS = ["hero", "about", "video-wish", "memories"];

function HomePage() {
  const [active, setActive] = useState("hero");
  const [unlocked, setUnlocked] = useState(() => {
    try { return localStorage.getItem("saloni_gate_unlocked_v1") === "yes"; } catch { return false; }
  });
  const location = useLocation();
  useLenis();

  useEffect(() => { loadSiteTexts().catch(() => {}); }, []);

  // Listen for unlock event so BirthdayReveal + AmbientPlayer fire after the gate opens
  useEffect(() => {
    const onStorage = () => {
      try { setUnlocked(localStorage.getItem("saloni_gate_unlocked_v1") === "yes"); } catch { /* noop */ }
    };
    window.addEventListener("storage", onStorage);
    // Also poll once after mount in case gate unlocks within the same tab
    const t = setInterval(onStorage, 500);
    return () => { window.removeEventListener("storage", onStorage); clearInterval(t); };
  }, []);

  const navigateTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { threshold: 0.35, rootMargin: "-80px 0px -40% 0px" }
    );
    SECTIONS.forEach((id) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [location.pathname]);

  return (
    <PasscodeGate>
      <div className="App" data-testid="birthday-app">
        {unlocked && <BirthdayReveal />}
        <Nav active={active} onNavigate={navigateTo} />
        <main>
          <Hero onExplore={navigateTo} />
          <About />
          <VideoWish />
          <MemoryLane />
        </main>
        <Footer />
      </div>
    </PasscodeGate>
  );
}

function PersistentOverlays() {
  // Renders on every route once the user has unlocked the gate. This keeps
  // ambient music, wallpaper CTA and cursor sparkles alive across route changes
  // (home ↔ memory detail) so music ducking + sparkles work everywhere.
  const location = useLocation();
  const [unlocked, setUnlocked] = useState(() => {
    try { return localStorage.getItem("saloni_gate_unlocked_v1") === "yes"; } catch { return false; }
  });
  useEffect(() => {
    const check = () => {
      try { setUnlocked(localStorage.getItem("saloni_gate_unlocked_v1") === "yes"); } catch { /* noop */ }
    };
    window.addEventListener("storage", check);
    window.addEventListener("saloni:unlocked", check);
    const t = setInterval(check, 800);
    return () => { window.removeEventListener("storage", check); window.removeEventListener("saloni:unlocked", check); clearInterval(t); };
  }, []);
  // Skip overlays on the admin route — they don't fit that context.
  if (!unlocked || location.pathname.startsWith("/admin")) return null;
  return (
    <>
      <AmbientPlayer />
      <WallpaperCard />
      <CursorSparkles global />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <PersistentOverlays />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/memory/:id" element={<MemoryDetail />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
